import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Server, TestTube, CheckCircle, XCircle, Mail, Globe, Shield, MessageSquare,
  Phone, Loader2, AlertCircle, Plus, Edit2, Trash2, Cpu, Search, Zap,
  CheckCircle2, ExternalLink, RefreshCw, Lock, X
} from 'lucide-react';
import { getSMTPSettings, saveSMTPSettings, testSMTP, getLLMProviders, createLLMProvider, updateLLMProvider, deleteLLMProvider, discoverModels, testLLMProvider, getCommercialKeys, saveCommercialKey, deleteCommercialKey, testCommercialKey } from '../lib/api';
import api from '../lib/api';

const PROVIDER_PRESETS = [
  { id: 'ollama', name: 'Ollama', default_url: 'http://localhost:11434', description: 'Run open-source models locally. No API key needed.', icon: '🦙' },
  { id: 'vllm', name: 'vLLM', default_url: 'http://localhost:8000', description: 'High-throughput serving engine for LLMs.', icon: '⚡' },
  { id: 'llamacpp', name: 'llama.cpp Server', default_url: 'http://localhost:8080', description: 'Lightweight C++ inference server.', icon: '🔧' },
  { id: 'lmstudio', name: 'LM Studio', default_url: 'http://localhost:1234', description: 'Desktop app for running local models.', icon: '📦' },
  { id: 'openrouter', name: 'OpenRouter', default_url: 'https://openrouter.ai/api', description: 'Access 100+ models through one API.', icon: '🌐' },
  { id: 'together', name: 'Together AI', default_url: 'https://api.together.xyz', description: 'Fast inference for open-source models.', icon: '🤝' },
  { id: 'groq', name: 'Groq', default_url: 'https://api.groq.com/openai', description: 'Ultra-fast inference on custom hardware.', icon: '🚀' },
  { id: 'custom', name: 'Custom OpenAI-Compatible', default_url: '', description: 'Any server with an OpenAI-compatible API.', icon: '🔌' },
];

// ── General Settings Tab ──
const GeneralSettingsTab = () => {
  const [customDomain, setCustomDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/api/settings/general').then(res => {
      setCustomDomain(res.data.custom_domain || '');
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/api/settings/general', { custom_domain: customDomain });
      toast.success('Settings saved! Public URLs will now use your custom domain.');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[hsl(var(--pane))] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Globe className="w-4 h-4 text-[hsl(var(--accent))]" /> Custom Domain</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Set your custom domain for public-facing URLs (campaign pages, forms, tracking links). 
            Make sure your DNS points to this application before configuring.
          </p>
          <div className="flex items-center gap-2">
            <Input
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              placeholder="campaign.securado.net"
              className="flex-1 bg-secondary/40"
              data-testid="custom-domain-input"
              disabled={!loaded}
            />
            <Button onClick={handleSave} disabled={saving} data-testid="save-domain-btn" className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
              Save
            </Button>
          </div>
          {customDomain && (
            <div className="flex items-center gap-2 text-[10px]">
              <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-400 border-emerald-500/25">Active</Badge>
              <span className="text-muted-foreground">Public URLs will use: <code className="bg-secondary px-1 py-0.5 rounded text-[9px]">https://{customDomain}/api/public/campaign/...</code></span>
            </div>
          )}
          {!customDomain && loaded && (
            <p className="text-[10px] text-muted-foreground/60">
              No custom domain configured. Public URLs will use the default application domain.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default function Settings() {
  const [smtp, setSmtp] = useState({
    host: '', port: 587, username: '', password: '',
    from_name: 'CipherCampaign', from_email: '', use_tls: true,
  });
  const [configured, setConfigured] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mailProvider, setMailProvider] = useState('smtp');
  const [o365Config, setO365Config] = useState({ tenant_id: '', client_id: '', client_secret: '', sender_email: '', reply_to: '' });
  const [sendgridConfig, setSendgridConfig] = useState({ api_key: '', sender_email: '', sender_name: '' });
  const [providerSaving, setProviderSaving] = useState(false);

  // Delivery Channels
  const [teamsWebhook, setTeamsWebhook] = useState('');
  const [teamsChannel, setTeamsChannel] = useState('');
  const [teamsConfigured, setTeamsConfigured] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappKey, setWhatsappKey] = useState('');
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [channelSaving, setChannelSaving] = useState(false);
  const [teamsTesting, setTeamsTesting] = useState(false);
  const [connectionTesting, setConnectionTesting] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);

  // LLM Providers
  const [llmProviders, setLlmProviders] = useState([]);
  const [llmLoading, setLlmLoading] = useState(false);
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [providerForm, setProviderForm] = useState({
    name: '', type: 'openai_compatible', base_url: '', api_key: '', models: [], preset: '',
  });
  const [discovering, setDiscovering] = useState(false);
  const [providerTesting, setProviderTesting] = useState(false);
  const [providerTestResult, setProviderTestResult] = useState(null);
  const [testModel, setTestModel] = useState('');

  // Commercial API Keys
  const [commercialKeys, setCommercialKeys] = useState({});
  const [commercialKeyInputs, setCommercialKeyInputs] = useState({});
  const [commercialTesting, setCommercialTesting] = useState({});
  const [commercialTestResults, setCommercialTestResults] = useState({});
  const [commercialSaving, setCommercialSaving] = useState({});

  // Microsoft SSO
  const [ssoConfig, setSsoConfig] = useState({ enabled: false, tenant_id: '', client_id: '', client_secret: '', allowed_domains: [], default_role: 'marketer' });
  const [ssoSaving, setSsoSaving] = useState(false);
  const [ssoDomainInput, setSsoDomainInput] = useState('');

  useEffect(() => { loadSettings(); loadMailProvider(); loadChannels(); loadLLMProviders(); loadCommercialKeys(); loadSSOConfig(); }, []);

  // ---- Channels ----
  const loadChannels = async () => {
    try {
      const res = await api.get('/api/settings/channels');
      if (res.data.teams) { setTeamsWebhook(res.data.teams.webhook_url || ''); setTeamsChannel(res.data.teams.channel_name || ''); setTeamsConfigured(res.data.teams.configured); }
      if (res.data.whatsapp) { setWhatsappPhone(res.data.whatsapp.phone_number || ''); setWhatsappConfigured(res.data.whatsapp.configured); }
    } catch (err) { console.error(err); }
  };

  const saveTeamsConfig = async () => {
    setChannelSaving(true);
    try { await api.put('/api/settings/channels/teams', { webhook_url: teamsWebhook, channel_name: teamsChannel }); setTeamsConfigured(!!teamsWebhook); toast.success('MS Teams webhook saved'); }
    catch (err) { toast.error('Failed to save Teams config'); } finally { setChannelSaving(false); }
  };

  const testTeamsWebhook = async () => {
    setTeamsTesting(true);
    try { const res = await api.post('/api/deliver/teams/test'); if (res.data.success) toast.success('Test message sent to Teams!'); else toast.error(res.data.error || 'Test failed'); }
    catch (err) { toast.error('Failed to test Teams webhook'); } finally { setTeamsTesting(false); }
  };

  const saveWhatsappConfig = async () => {
    setChannelSaving(true);
    try { await api.put('/api/settings/channels/whatsapp', { phone_number: whatsappPhone, api_key: whatsappKey }); setWhatsappConfigured(!!whatsappPhone); toast.success('WhatsApp config saved'); }
    catch (err) { toast.error('Failed to save WhatsApp config'); } finally { setChannelSaving(false); }
  };

  const testEmailConnection = async () => {
    setConnectionTesting(true); setConnectionResult(null);
    try {
      const res = await api.post('/api/email/test-connection');
      setConnectionResult(res.data);
      if (res.data.status === 'success') toast.success('Email connection successful!');
      else toast.error(res.data.message || 'Connection failed');
    } catch (err) {
      setConnectionResult({ status: 'error', message: err.response?.data?.detail || 'Connection failed' });
      toast.error('Connection test failed');
    } finally { setConnectionTesting(false); }
  };

  const loadMailProvider = async () => {
    try {
      const res = await api.get('/api/settings/mail-provider');
      if (res.data) { setMailProvider(res.data.provider || 'smtp'); if (res.data.o365) setO365Config(prev => ({ ...prev, ...res.data.o365 })); if (res.data.sendgrid) setSendgridConfig(prev => ({ ...prev, ...res.data.sendgrid })); }
    } catch (err) { console.error(err); }
  };

  const handleSaveMailProvider = async () => {
    setProviderSaving(true);
    try { await api.post('/api/settings/mail-provider', { provider: mailProvider, o365: o365Config, sendgrid: sendgridConfig }); toast.success(`Mail provider set to ${mailProvider.toUpperCase()}`); }
    catch (err) { toast.error('Failed to save mail provider'); } finally { setProviderSaving(false); }
  };

  const loadSettings = async () => {
    try {
      const res = await getSMTPSettings();
      if (res.data.configured) { setConfigured(true); setSmtp({ host: res.data.host || '', port: res.data.port || 587, username: res.data.username || '', password: '', from_name: res.data.from_name || 'CipherCampaign', from_email: res.data.from_email || '', use_tls: res.data.use_tls !== false }); }
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    if (!smtp.host || !smtp.from_email) { toast.error('Host and From Email are required'); return; }
    setSaving(true);
    try { await saveSMTPSettings(smtp); setConfigured(true); toast.success('SMTP settings saved'); }
    catch (err) { toast.error('Failed to save settings'); } finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Enter a test email address'); return; }
    setTesting(true);
    try { const res = await testSMTP({ to_email: testEmail }); if (res.data.success) toast.success('Test email sent successfully!'); else toast.error(res.data.message || 'Test failed'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); } finally { setTesting(false); }
  };

  // ---- Microsoft SSO ----
  const loadSSOConfig = async () => {
    try {
      const res = await api.get('/api/settings/sso/microsoft');
      if (res.data) setSsoConfig(prev => ({ ...prev, ...res.data, client_secret: '' }));
    } catch (err) { console.error(err); }
  };

  const handleSaveSSO = async () => {
    setSsoSaving(true);
    try {
      const payload = { ...ssoConfig };
      if (!payload.client_secret) delete payload.client_secret;
      await api.post('/api/settings/sso/microsoft', payload);
      toast.success(ssoConfig.enabled ? 'Microsoft SSO enabled' : 'Microsoft SSO disabled');
    } catch (err) {
      toast.error('Failed to save SSO settings');
    } finally { setSsoSaving(false); }
  };

  const addSSODomain = () => {
    if (ssoDomainInput.trim() && !ssoConfig.allowed_domains.includes(ssoDomainInput.trim())) {
      setSsoConfig(prev => ({ ...prev, allowed_domains: [...prev.allowed_domains, ssoDomainInput.trim()] }));
      setSsoDomainInput('');
    }
  };

  const removeSSODomain = (domain) => {
    setSsoConfig(prev => ({ ...prev, allowed_domains: prev.allowed_domains.filter(d => d !== domain) }));
  };


  // ---- LLM Providers ----
  const loadLLMProviders = async () => {
    setLlmLoading(true);
    try {
      const res = await getLLMProviders();
      setLlmProviders(res.data?.providers || []);
    } catch (err) { console.error(err); } finally { setLlmLoading(false); }
  };

  const openProviderDialog = (provider = null) => {
    if (provider) {
      setEditingProvider(provider);
      setProviderForm({
        name: provider.name || '', type: provider.type || 'openai_compatible',
        base_url: provider.base_url || '', api_key: '', models: provider.models || [], preset: '',
      });
    } else {
      setEditingProvider(null);
      setProviderForm({ name: '', type: 'openai_compatible', base_url: '', api_key: '', models: [], preset: '' });
    }
    setProviderTestResult(null);
    setTestModel('');
    setShowProviderDialog(true);
  };

  const applyPreset = (presetId) => {
    const preset = PROVIDER_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setProviderForm(prev => ({
        ...prev, name: preset.name, base_url: preset.default_url, preset: presetId,
      }));
    }
  };

  const handleDiscoverModels = async () => {
    if (!providerForm.base_url) { toast.error('Enter a base URL first'); return; }
    setDiscovering(true);
    try {
      const res = await discoverModels({ base_url: providerForm.base_url, api_key: providerForm.api_key });
      if (res.data.status === 'success') {
        setProviderForm(prev => ({ ...prev, models: res.data.models }));
        toast.success(`Found ${res.data.model_count} models`);
      } else {
        toast.error(res.data.message || 'Model discovery failed');
      }
    } catch (err) { toast.error('Failed to discover models'); } finally { setDiscovering(false); }
  };

  const handleTestProvider = async () => {
    if (!testModel && providerForm.models.length === 0) { toast.error('No model to test. Discover models first.'); return; }
    setProviderTesting(true); setProviderTestResult(null);
    try {
      const res = await testLLMProvider({
        base_url: providerForm.base_url, api_key: providerForm.api_key,
        model: testModel || providerForm.models[0]?.id, type: providerForm.type,
      });
      setProviderTestResult(res.data);
      if (res.data.status === 'success') toast.success(`Model responded in ${res.data.duration_s}s`);
      else toast.error(res.data.message || 'Test failed');
    } catch (err) { toast.error('Provider test failed'); setProviderTestResult({ status: 'error', message: err.message }); }
    finally { setProviderTesting(false); }
  };

  const handleSaveProvider = async () => {
    if (!providerForm.name.trim()) { toast.error('Provider name is required'); return; }
    if (!providerForm.base_url.trim()) { toast.error('Base URL is required'); return; }
    try {
      if (editingProvider) {
        await updateLLMProvider(editingProvider.id, providerForm);
        toast.success('Provider updated');
      } else {
        await createLLMProvider(providerForm);
        toast.success('Provider added');
      }
      setShowProviderDialog(false);
      loadLLMProviders();
    } catch (err) { toast.error('Failed to save provider'); }
  };

  const handleDeleteProvider = async (id) => {
    if (!window.confirm('Remove this LLM provider? Agents using it will fall back to defaults.')) return;
    try { await deleteLLMProvider(id); toast.success('Provider removed'); loadLLMProviders(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const addManualModel = () => {
    const name = prompt('Enter model name (e.g., llama3.2, mistral-7b):');
    if (name) {
      setProviderForm(prev => ({
        ...prev, models: [...prev.models, { id: name.trim(), name: name.trim() }],
      }));
    }
  };

  const removeModel = (idx) => {
    setProviderForm(prev => ({
      ...prev, models: prev.models.filter((_, i) => i !== idx),
    }));
  };

  const builtinProviders = llmProviders.filter(p => p.builtin);
  const customProviders = llmProviders.filter(p => !p.builtin);

  // ---- Commercial API Keys ----
  const loadCommercialKeys = async () => {
    try {
      const res = await getCommercialKeys();
      setCommercialKeys(res.data || {});
    } catch (err) { console.error(err); }
  };

  const handleSaveCommercialKey = async (providerId) => {
    const key = commercialKeyInputs[providerId];
    if (!key) { toast.error('Enter an API key'); return; }
    setCommercialSaving(prev => ({ ...prev, [providerId]: true }));
    try {
      await saveCommercialKey({ provider: providerId, api_key: key, enabled: true });
      toast.success(`${commercialKeys[providerId]?.name || providerId} key saved`);
      setCommercialKeyInputs(prev => ({ ...prev, [providerId]: '' }));
      loadCommercialKeys();
    } catch (err) { toast.error('Failed to save key'); }
    finally { setCommercialSaving(prev => ({ ...prev, [providerId]: false })); }
  };

  const handleToggleCommercialKey = async (providerId, enabled) => {
    try {
      await saveCommercialKey({ provider: providerId, enabled });
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} direct ${providerId} key`);
      loadCommercialKeys();
    } catch (err) { toast.error('Failed to toggle'); }
  };

  const handleTestCommercialKey = async (providerId, model) => {
    setCommercialTesting(prev => ({ ...prev, [providerId]: true }));
    setCommercialTestResults(prev => ({ ...prev, [providerId]: null }));
    try {
      const res = await testCommercialKey({
        provider: providerId,
        api_key: commercialKeyInputs[providerId] || '',
        model: model,
      });
      setCommercialTestResults(prev => ({ ...prev, [providerId]: res.data }));
      if (res.data.status === 'success') toast.success(`${providerId} key verified!`);
      else toast.error(res.data.message || 'Test failed');
    } catch (err) {
      setCommercialTestResults(prev => ({ ...prev, [providerId]: { status: 'error', message: err.message } }));
      toast.error('Test failed');
    }
    finally { setCommercialTesting(prev => ({ ...prev, [providerId]: false })); }
  };

  const handleRemoveCommercialKey = async (providerId) => {
    if (!window.confirm('Remove this API key? Agents will fall back to Emergent key.')) return;
    try {
      await deleteCommercialKey(providerId);
      toast.success('Key removed');
      loadCommercialKeys();
    } catch (err) { toast.error('Failed to remove'); }
  };

  return (
    <div className="page-container">
      {/* Hero Header */}
      <div className="page-hero" data-testid="settings-hero">
        <div className="relative z-10 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0">
              <Server className="w-6 h-6 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand))] font-semibold mb-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Configuration</p>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="settings-title">Settings</h1>
              <p className="text-sm text-white/50 mt-0.5">Configure LLM providers, email delivery, integrations, and platform settings</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general"><Globe className="w-4 h-4 mr-1.5" /> General</TabsTrigger>
          <TabsTrigger value="llm" data-testid="tab-llm"><Cpu className="w-4 h-4 mr-1.5" /> LLM Providers</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email"><Mail className="w-4 h-4 mr-1.5" /> Email Delivery</TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels"><MessageSquare className="w-4 h-4 mr-1.5" /> Delivery Channels</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations"><Globe className="w-4 h-4 mr-1.5" /> Integrations</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security"><Lock className="w-4 h-4 mr-1.5" /> Security</TabsTrigger>
        </TabsList>


        {/* ====== GENERAL TAB ====== */}
        <TabsContent value="general">
          <GeneralSettingsTab />
        </TabsContent>

        {/* ====== LLM PROVIDERS TAB ====== */}
        <TabsContent value="llm">
          <div className="space-y-6">
            {/* Built-in Providers */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-[hsl(var(--accent))]" />
                <h3 className="text-sm font-semibold">Built-in Providers</h3>
                <Badge variant="outline" className="text-[10px] bg-[hsl(var(--accent)/0.1)] text-[hsl(var(--accent))] border-[hsl(var(--accent)/0.25)]">via Emergent Key</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {builtinProviders.map(p => (
                  <Card key={p.id} className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}} data-testid={`builtin-provider-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-sm">{p.name}</h4>
                        <Badge className="text-[9px] bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.3)]">
                          <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Active
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        {(p.models || []).map(m => (
                          <div key={m.id} className="text-xs font-mono text-muted-foreground bg-secondary/30 rounded px-2 py-1">{m.name || m.id}</div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <Separator />

            {/* Commercial API Keys */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-semibold">Commercial API Keys</h3>
                <span className="text-xs text-muted-foreground">(Use your own provider keys directly)</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                When enabled, agents using these providers will use your direct API key instead of the Emergent universal key. This gives you full control over billing and rate limits.
              </p>
              <div className="space-y-3">
                {Object.entries(commercialKeys).map(([providerId, providerData]) => {
                  const testResult = commercialTestResults[providerId];
                  return (
                    <Card key={providerId} className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}} data-testid={`commercial-key-${providerId}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-sm">{providerData.name}</h4>
                            {providerData.configured ? (
                              <Badge className="text-[9px] bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.3)]">
                                <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Key Set
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px]">No Key</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {providerData.configured && (
                              <>
                                <Label className="text-xs text-muted-foreground">Use Direct Key</Label>
                                <Switch
                                  checked={providerData.enabled}
                                  onCheckedChange={(v) => handleToggleCommercialKey(providerId, v)}
                                  data-testid={`toggle-${providerId}`}
                                />
                              </>
                            )}
                          </div>
                        </div>
                        {providerData.configured && (
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs font-mono text-muted-foreground bg-secondary/30 rounded px-2 py-1">{providerData.key_preview}</span>
                            <Button variant="ghost" size="sm" className="h-6 text-xs text-destructive" onClick={() => handleRemoveCommercialKey(providerId)} data-testid={`remove-key-${providerId}`}>
                              <Trash2 className="w-3 h-3 mr-1" /> Remove
                            </Button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Input
                            value={commercialKeyInputs[providerId] || ''}
                            onChange={(e) => setCommercialKeyInputs(prev => ({...prev, [providerId]: e.target.value}))}
                            type="password"
                            placeholder={`Enter ${providerData.name} API key...`}
                            className="bg-secondary/40 text-xs font-mono flex-1"
                            data-testid={`input-key-${providerId}`}
                          />
                          <Button size="sm" className="bg-[hsl(var(--accent))] text-white text-xs h-9" onClick={() => handleSaveCommercialKey(providerId)} disabled={commercialSaving[providerId]} data-testid={`save-key-${providerId}`}>
                            {commercialSaving[providerId] ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Key'}
                          </Button>
                          {providerData.configured && (
                            <Select
                              defaultValue={providerData.models?.[0]?.id}
                              onValueChange={() => {}}
                            >
                              <SelectTrigger className="w-36 bg-secondary/40 text-xs h-9"><SelectValue placeholder="Model" /></SelectTrigger>
                              <SelectContent>
                                {(providerData.models || []).map(m => (
                                  <SelectItem key={m.id} value={m.id} className="text-xs font-mono">{m.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {providerData.configured && (
                            <Button variant="outline" size="sm" className="text-xs h-9" onClick={() => handleTestCommercialKey(providerId, providerData.models?.[0]?.id)} disabled={commercialTesting[providerId]} data-testid={`test-key-${providerId}`}>
                              {commercialTesting[providerId] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><TestTube className="w-3 h-3 mr-1" /> Test</>}
                            </Button>
                          )}
                        </div>
                        {/* Test result */}
                        {testResult && (
                          <div className={`mt-2 p-2 rounded-lg text-xs ${testResult.status === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-destructive/10 border border-destructive/20'}`}>
                            <div className="flex items-center gap-2">
                              {testResult.status === 'success' ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-destructive" />}
                              <span>{testResult.message}</span>
                              {testResult.duration_s && <span className="text-muted-foreground ml-auto">{testResult.duration_s}s</span>}
                            </div>
                            {testResult.response && <p className="mt-1 font-mono text-muted-foreground bg-secondary/30 rounded p-1.5">"{testResult.response}"</p>}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Custom Providers */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-[hsl(var(--accent))]" />
                  <h3 className="text-sm font-semibold">Custom Providers</h3>
                  <span className="text-xs text-muted-foreground">(Ollama, vLLM, llama.cpp, OpenRouter, Groq, etc.)</span>
                </div>
                <Button size="sm" onClick={() => openProviderDialog()} className="bg-[hsl(var(--accent))] text-white text-xs" data-testid="add-provider-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Provider
                </Button>
              </div>

              {customProviders.length === 0 ? (
                <Card className="bg-card/90 border border-border border-dashed" style={{boxShadow: 'var(--shadow-elev-1)'}}>
                  <CardContent className="text-center py-10">
                    <Server className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground mb-1">No custom LLM providers configured</p>
                    <p className="text-xs text-muted-foreground mb-4">Add your Ollama, vLLM, llama.cpp, or any OpenAI-compatible server</p>
                    <Button size="sm" variant="outline" onClick={() => openProviderDialog()} className="text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Your First Provider
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {customProviders.map(p => (
                    <Card key={p.id} className="bg-card/90 border border-border hover:border-[hsl(var(--accent)/0.4)] transition-colors" style={{boxShadow: 'var(--shadow-elev-1)'}} data-testid={`custom-provider-${p.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-sm">{p.name}</h4>
                              <Badge variant="outline" className="text-[9px]">{p.type}</Badge>
                              <Badge className={`text-[9px] ${p.status === 'active' ? 'bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.3)]' : 'bg-amber-500/10 text-amber-400 border border-amber-500/25'}`}>
                                {p.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground font-mono mt-1">{p.base_url}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openProviderDialog(p)}>
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteProvider(p.id)}>
                              <Trash2 className="w-3.5 h-3.5 text-[hsl(var(--status-danger))]" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(p.models || []).map(m => (
                            <Badge key={m.id} variant="outline" className="text-[10px] font-mono">{m.name || m.id}</Badge>
                          ))}
                          {(p.models || []).length === 0 && (
                            <span className="text-[10px] text-muted-foreground">No models configured</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-[10px] text-muted-foreground">{p.api_key_set ? 'API key set' : 'No API key'}</span>
                          <span className="text-[10px] text-muted-foreground">{(p.models || []).length} model{(p.models || []).length !== 1 ? 's' : ''}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Setup Guide */}
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Quick Setup Guides</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {PROVIDER_PRESETS.slice(0, 4).map(preset => (
                    <div key={preset.id} className="bg-secondary/30 rounded-lg p-3 hover:bg-secondary/50 transition-colors cursor-pointer" onClick={() => { openProviderDialog(); setTimeout(() => applyPreset(preset.id), 100); }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{preset.icon}</span>
                        <span className="text-xs font-semibold">{preset.name}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{preset.description}</p>
                      <code className="text-[9px] text-muted-foreground/70 font-mono mt-1 block">{preset.default_url}</code>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ====== EMAIL TAB (unchanged) ====== */}
        <TabsContent value="email">
          <Card className="bg-card/90 border border-border mb-4" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[hsl(var(--accent))]" />
                  <CardTitle className="text-sm font-medium">Mail Provider</CardTitle>
                  <Badge variant="outline" className="text-[10px] capitalize">{mailProvider}</Badge>
                </div>
                <Button size="sm" onClick={handleSaveMailProvider} disabled={providerSaving} className="bg-[hsl(var(--accent))] text-white text-xs h-8" data-testid="save-mail-provider-btn">
                  {providerSaving ? 'Saving...' : 'Save Provider'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Active Provider</Label>
                <Select value={mailProvider} onValueChange={setMailProvider}>
                  <SelectTrigger className="bg-secondary/40" data-testid="mail-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="smtp">SMTP (Custom Server)</SelectItem>
                    <SelectItem value="o365">Microsoft 365 / Office 365</SelectItem>
                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {mailProvider === 'o365' && (
                <div className="space-y-3 p-3 rounded-lg bg-secondary/20 border border-border">
                  <div className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-blue-400" /><span className="text-xs font-semibold text-blue-400">Microsoft 365 Configuration</span></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Tenant ID</Label><Input value={o365Config.tenant_id} onChange={(e) => setO365Config({...o365Config, tenant_id: e.target.value})} placeholder="your-tenant-id" className="bg-secondary/40 text-xs" data-testid="o365-tenant-id" /></div>
                    <div><Label className="text-xs">Client ID</Label><Input value={o365Config.client_id} onChange={(e) => setO365Config({...o365Config, client_id: e.target.value})} placeholder="your-client-id" className="bg-secondary/40 text-xs" data-testid="o365-client-id" /></div>
                    <div><Label className="text-xs">Client Secret</Label><Input value={o365Config.client_secret} onChange={(e) => setO365Config({...o365Config, client_secret: e.target.value})} type="password" className="bg-secondary/40 text-xs" data-testid="o365-client-secret" /></div>
                    <div><Label className="text-xs">Sender Email</Label><Input value={o365Config.sender_email} onChange={(e) => setO365Config({...o365Config, sender_email: e.target.value})} placeholder="noreply@company.com" className="bg-secondary/40 text-xs" data-testid="o365-sender" /></div>
                  </div>
                  <div><Label className="text-xs">Reply-To</Label><Input value={o365Config.reply_to} onChange={(e) => setO365Config({...o365Config, reply_to: e.target.value})} placeholder="support@company.com" className="bg-secondary/40 text-xs" /></div>
                  <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                    <Button size="sm" variant="outline" onClick={testEmailConnection} disabled={connectionTesting} className="text-xs h-7" data-testid="test-o365-connection-btn">
                      {connectionTesting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing...</> : <><TestTube className="w-3 h-3 mr-1" /> Test O365 Connection</>}
                    </Button>
                    {connectionResult && connectionResult.provider === 'o365' && (
                      <span className={`text-[10px] ${connectionResult.status === 'success' ? 'text-emerald-400' : 'text-destructive'}`}>
                        {connectionResult.status === 'success' ? 'Connected' : connectionResult.message?.substring(0, 60)}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {mailProvider === 'sendgrid' && (
                <div className="space-y-3 p-3 rounded-lg bg-secondary/20 border border-border">
                  <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-emerald-400" /><span className="text-xs font-semibold text-emerald-400">SendGrid Configuration</span></div>
                  <div><Label className="text-xs">API Key</Label><Input value={sendgridConfig.api_key} onChange={(e) => setSendgridConfig({...sendgridConfig, api_key: e.target.value})} type="password" className="bg-secondary/40 text-xs" data-testid="sendgrid-api-key" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Sender Email</Label><Input value={sendgridConfig.sender_email} onChange={(e) => setSendgridConfig({...sendgridConfig, sender_email: e.target.value})} className="bg-secondary/40 text-xs" /></div>
                    <div><Label className="text-xs">Sender Name</Label><Input value={sendgridConfig.sender_name} onChange={(e) => setSendgridConfig({...sendgridConfig, sender_name: e.target.value})} className="bg-secondary/40 text-xs" /></div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                    <Button size="sm" variant="outline" onClick={testEmailConnection} disabled={connectionTesting} className="text-xs h-7" data-testid="test-sendgrid-connection-btn">
                      {connectionTesting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing...</> : <><TestTube className="w-3 h-3 mr-1" /> Test SendGrid Connection</>}
                    </Button>
                    {connectionResult && connectionResult.provider === 'sendgrid' && (
                      <span className={`text-[10px] ${connectionResult.status === 'success' ? 'text-emerald-400' : 'text-destructive'}`}>
                        {connectionResult.status === 'success' ? 'Connected' : connectionResult.message?.substring(0, 60)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {mailProvider === 'smtp' && (
            <>
              <div className="mb-6">
                {configured ? (
                  <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-[hsl(var(--status-success))]" /><span className="text-[hsl(var(--status-success))]">SMTP configured</span></div>
                ) : (
                  <div className="flex items-center gap-2 text-sm"><XCircle className="w-4 h-4 text-muted-foreground" /><span className="text-muted-foreground">SMTP not configured</span></div>
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
                  <CardHeader className="pb-3"><div className="flex items-center gap-2"><Server className="w-4 h-4 text-muted-foreground" /><CardTitle className="text-sm font-medium">SMTP Configuration</CardTitle></div></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>SMTP Host</Label><Input value={smtp.host} onChange={(e) => setSmtp({...smtp, host: e.target.value})} placeholder="smtp.gmail.com" className="bg-secondary/40" data-testid="smtp-host-input" /></div>
                      <div><Label>Port</Label><Input type="number" value={smtp.port} onChange={(e) => setSmtp({...smtp, port: parseInt(e.target.value)})} placeholder="587" className="bg-secondary/40" data-testid="smtp-port-input" /></div>
                    </div>
                    <div><Label>Username</Label><Input value={smtp.username} onChange={(e) => setSmtp({...smtp, username: e.target.value})} placeholder="your@email.com" className="bg-secondary/40" data-testid="smtp-username-input" /></div>
                    <div><Label>Password</Label><Input type="password" value={smtp.password} onChange={(e) => setSmtp({...smtp, password: e.target.value})} placeholder="App password or SMTP password" className="bg-secondary/40" data-testid="smtp-password-input" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>From Name</Label><Input value={smtp.from_name} onChange={(e) => setSmtp({...smtp, from_name: e.target.value})} placeholder="CipherCampaign" className="bg-secondary/40" /></div>
                      <div><Label>From Email</Label><Input value={smtp.from_email} onChange={(e) => setSmtp({...smtp, from_email: e.target.value})} placeholder="noreply@yourdomain.com" className="bg-secondary/40" /></div>
                    </div>
                    <div className="flex items-center gap-2"><Switch checked={smtp.use_tls} onCheckedChange={(v) => setSmtp({...smtp, use_tls: v})} /><Label>Use TLS</Label></div>
                    <Button onClick={handleSave} disabled={saving} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="save-smtp-btn">{saving ? 'Saving...' : 'Save SMTP Settings'}</Button>
                  </CardContent>
                </Card>
                <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
                  <CardHeader className="pb-3"><div className="flex items-center gap-2"><TestTube className="w-4 h-4 text-muted-foreground" /><CardTitle className="text-sm font-medium">Test SMTP</CardTitle></div></CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">Send a test email to verify your SMTP settings.</p>
                    <div><Label>Test Email Address</Label><Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" className="bg-secondary/40" /></div>
                    <Button onClick={handleTest} disabled={testing || !configured} variant="outline" className="w-full" data-testid="smtp-test-button"><TestTube className="w-4 h-4 mr-2" />{testing ? 'Sending...' : 'Send Test Email'}</Button>
                    {!configured && <p className="text-xs text-muted-foreground">Save SMTP settings first</p>}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ====== CHANNELS TAB (unchanged) ====== */}
        <TabsContent value="channels">
          <div className="grid gap-4">
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center"><MessageSquare className="w-4 h-4 text-blue-400" /></div>
                  <div><CardTitle className="text-sm font-medium">Microsoft Teams</CardTitle><p className="text-[10px] text-muted-foreground">Send via incoming webhook</p></div>
                  <Badge variant="outline" className={`ml-auto text-[10px] ${teamsConfigured ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' : 'bg-amber-500/10 text-amber-400 border-amber-500/25'}`}>{teamsConfigured ? 'Connected' : 'Not Configured'}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div><Label className="text-xs">Webhook URL</Label><Input value={teamsWebhook} onChange={(e) => setTeamsWebhook(e.target.value)} placeholder="https://outlook.office.com/webhook/..." className="bg-secondary/40 text-xs" data-testid="teams-webhook-url" /><p className="text-[10px] text-muted-foreground mt-1">Channel Settings &gt; Connectors &gt; Incoming Webhook</p></div>
                <div><Label className="text-xs">Channel Name</Label><Input value={teamsChannel} onChange={(e) => setTeamsChannel(e.target.value)} placeholder="#marketing-alerts" className="bg-secondary/40 text-xs" data-testid="teams-channel-name" /></div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveTeamsConfig} disabled={channelSaving} className="bg-[hsl(var(--accent))] text-white text-xs h-8" data-testid="save-teams-btn">{channelSaving ? 'Saving...' : 'Save'}</Button>
                  {teamsConfigured && <Button size="sm" variant="outline" onClick={testTeamsWebhook} disabled={teamsTesting} className="text-xs h-8" data-testid="test-teams-btn">{teamsTesting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <TestTube className="w-3 h-3 mr-1" />}Send Test</Button>}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Phone className="w-4 h-4 text-emerald-400" /></div>
                  <div><CardTitle className="text-sm font-medium">WhatsApp Business</CardTitle><p className="text-[10px] text-muted-foreground">Send via WhatsApp Business API</p></div>
                  <Badge variant="outline" className={`ml-auto text-[10px] ${whatsappConfigured ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' : 'bg-amber-500/10 text-amber-400 border-amber-500/25'}`}>{whatsappConfigured ? 'Connected' : 'Not Configured'}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div><Label className="text-xs">Business Phone</Label><Input value={whatsappPhone} onChange={(e) => setWhatsappPhone(e.target.value)} placeholder="+1234567890" className="bg-secondary/40 text-xs" data-testid="whatsapp-phone" /></div>
                <div><Label className="text-xs">API Key</Label><Input value={whatsappKey} onChange={(e) => setWhatsappKey(e.target.value)} type="password" placeholder="Your API key" className="bg-secondary/40 text-xs" data-testid="whatsapp-api-key" /><p className="text-[10px] text-muted-foreground mt-1">Supports Twilio or Meta Cloud API</p></div>
                <Button size="sm" onClick={saveWhatsappConfig} disabled={channelSaving} className="bg-[hsl(var(--accent))] text-white text-xs h-8" data-testid="save-whatsapp-btn">{channelSaving ? 'Saving...' : 'Save'}</Button>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-3"><div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[hsl(var(--accent))]" /><CardTitle className="text-sm font-medium">Email Provider Test</CardTitle></div></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">Test your active email provider ({mailProvider.toUpperCase()}) connection.</p>
                <Button size="sm" variant="outline" onClick={testEmailConnection} disabled={connectionTesting} className="text-xs h-8" data-testid="test-email-connection-btn">
                  {connectionTesting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing...</> : <><TestTube className="w-3 h-3 mr-1" /> Test Connection</>}
                </Button>
                {connectionResult && (
                  <div className={`p-3 rounded-lg text-xs ${connectionResult.status === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-destructive/10 border border-destructive/20'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {connectionResult.status === 'success' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <AlertCircle className="w-3.5 h-3.5 text-destructive" />}
                      <span className="font-medium">{connectionResult.status === 'success' ? 'Success' : 'Failed'}</span>
                    </div>
                    <p className="text-muted-foreground">{connectionResult.message}</p>
                    {connectionResult.help && <p className="mt-1 text-amber-400 font-medium">{connectionResult.help}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ====== INTEGRATIONS TAB (unchanged) ====== */}
        <TabsContent value="integrations">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center"><Globe className="w-5 h-5 text-blue-400" /></div>
                  <div><h3 className="text-sm font-semibold">Microsoft 365</h3><p className="text-xs text-muted-foreground">OAuth2 email via Microsoft Graph API</p></div>
                  <Badge variant="outline" className={`ml-auto text-[10px] ${mailProvider === 'o365' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' : ''}`}>{mailProvider === 'o365' ? 'Active' : 'Not Configured'}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Switch to Email Delivery tab to configure.</p>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Shield className="w-5 h-5 text-emerald-400" /></div>
                  <div><h3 className="text-sm font-semibold">SendGrid</h3><p className="text-xs text-muted-foreground">Transactional & marketing email</p></div>
                  <Badge variant="outline" className={`ml-auto text-[10px] ${mailProvider === 'sendgrid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' : ''}`}>{mailProvider === 'sendgrid' ? 'Active' : 'Not Configured'}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Switch to Email Delivery tab to configure.</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ====== SECURITY TAB (SSO) ====== */}
        <TabsContent value="security">
          <div className="space-y-4">
            <Card className="bg-[hsl(var(--pane))] border border-white/10" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                      <svg className="w-4 h-4" viewBox="0 0 21 21" fill="none"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
                    </div>
                    <div>
                      <CardTitle className="text-sm font-medium">Microsoft SSO</CardTitle>
                      <p className="text-[10px] text-muted-foreground">Azure AD / Entra ID single sign-on</p>
                    </div>
                    <Badge variant="outline" className={`ml-2 text-[10px] ${ssoConfig.enabled ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' : ''}`}>
                      {ssoConfig.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={ssoConfig.enabled} onCheckedChange={(v) => setSsoConfig(prev => ({ ...prev, enabled: v }))} data-testid="sso-enabled-toggle" />
                    <Button size="sm" onClick={handleSaveSSO} disabled={ssoSaving} className="bg-[hsl(var(--accent))] text-white text-xs h-8" data-testid="save-sso-btn">
                      {ssoSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/15 text-xs text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Setup Instructions:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Go to <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps" target="_blank" rel="noreferrer" className="text-[hsl(var(--accent))] underline">Azure Portal &gt; App Registrations</a></li>
                    <li>Create a new registration (Web platform)</li>
                    <li>Set Redirect URI to: <code className="text-[10px] bg-secondary/60 px-1 rounded">{window.location.origin}/api/auth/sso/microsoft/callback</code></li>
                    <li>Under API Permissions, add <code className="text-[10px] bg-secondary/60 px-1 rounded">User.Read</code> (delegated)</li>
                    <li>Create a Client Secret under Certificates &amp; Secrets</li>
                  </ol>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Tenant ID</Label>
                    <Input value={ssoConfig.tenant_id} onChange={(e) => setSsoConfig(prev => ({ ...prev, tenant_id: e.target.value }))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="bg-secondary/40 text-xs" data-testid="sso-tenant-id" />
                  </div>
                  <div>
                    <Label className="text-xs">Client ID (Application ID)</Label>
                    <Input value={ssoConfig.client_id} onChange={(e) => setSsoConfig(prev => ({ ...prev, client_id: e.target.value }))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="bg-secondary/40 text-xs" data-testid="sso-client-id" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Client Secret</Label>
                  <Input value={ssoConfig.client_secret} onChange={(e) => setSsoConfig(prev => ({ ...prev, client_secret: e.target.value }))} type="password" placeholder={ssoConfig.client_secret_set ? '••••• (already set, leave blank to keep)' : 'Enter client secret'} className="bg-secondary/40 text-xs" data-testid="sso-client-secret" />
                </div>
                <div>
                  <Label className="text-xs">Allowed Email Domains (optional - leave empty to allow all)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={ssoDomainInput} onChange={(e) => setSsoDomainInput(e.target.value)} placeholder="company.com" className="bg-secondary/40 text-xs" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSSODomain())} data-testid="sso-domain-input" />
                    <Button size="sm" variant="outline" onClick={addSSODomain} className="text-xs h-8 shrink-0"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                  </div>
                  {ssoConfig.allowed_domains.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {ssoConfig.allowed_domains.map(d => (
                        <Badge key={d} variant="outline" className="text-[10px] gap-1">
                          {d}
                          <button onClick={() => removeSSODomain(d)} className="hover:text-destructive"><X className="w-2.5 h-2.5" /></button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Default Role for SSO Users</Label>
                  <Select value={ssoConfig.default_role} onValueChange={(v) => setSsoConfig(prev => ({ ...prev, default_role: v }))}>
                    <SelectTrigger className="bg-secondary/40 text-xs" data-testid="sso-default-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="marketer">Marketer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ====== ADD/EDIT PROVIDER DIALOG ====== */}
      <Dialog open={showProviderDialog} onOpenChange={setShowProviderDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-[hsl(var(--accent))]" />
              {editingProvider ? 'Edit LLM Provider' : 'Add LLM Provider'}
            </DialogTitle>
            <DialogDescription>
              Connect to Ollama, vLLM, llama.cpp, or any OpenAI-compatible endpoint
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Presets (only for new providers) */}
            {!editingProvider && (
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Quick Presets</Label>
                <div className="grid grid-cols-4 gap-2">
                  {PROVIDER_PRESETS.map(preset => (
                    <button key={preset.id} onClick={() => applyPreset(preset.id)}
                      className={`text-left p-2 rounded-lg border transition-colors text-xs ${providerForm.preset === preset.id ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.1)]' : 'border-border bg-secondary/30 hover:bg-secondary/50'}`}
                      data-testid={`preset-${preset.id}`}>
                      <span className="text-sm mr-1">{preset.icon}</span>
                      <span className="font-medium">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Provider Name *</Label>
                <Input value={providerForm.name} onChange={(e) => setProviderForm({...providerForm, name: e.target.value})} placeholder="My Ollama Server" className="bg-secondary/40" data-testid="provider-name-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={providerForm.type} onValueChange={(v) => setProviderForm({...providerForm, type: v})}>
                  <SelectTrigger className="bg-secondary/40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai_compatible">OpenAI-Compatible</SelectItem>
                    <SelectItem value="custom">Custom API</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Base URL *</Label>
              <Input value={providerForm.base_url} onChange={(e) => setProviderForm({...providerForm, base_url: e.target.value})} placeholder="http://localhost:11434" className="bg-secondary/40 font-mono text-sm" data-testid="provider-url-input" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Ollama: <code>http://host:11434</code> | vLLM: <code>http://host:8000</code> | llama.cpp: <code>http://host:8080</code>
              </p>
            </div>

            <div>
              <Label>API Key <span className="text-muted-foreground">(optional for Ollama/llama.cpp)</span></Label>
              <Input value={providerForm.api_key} onChange={(e) => setProviderForm({...providerForm, api_key: e.target.value})} type="password" placeholder="sk-... or leave empty" className="bg-secondary/40" data-testid="provider-api-key-input" />
            </div>

            <Separator />

            {/* Model Discovery */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Models</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={handleDiscoverModels} disabled={discovering || !providerForm.base_url} data-testid="discover-models-btn">
                    {discovering ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Discovering...</> : <><Search className="w-3 h-3 mr-1" /> Discover Models</>}
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={addManualModel} data-testid="add-model-manually-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Manually
                  </Button>
                </div>
              </div>

              {providerForm.models.length === 0 ? (
                <div className="text-center py-4 bg-secondary/20 rounded-lg border border-dashed border-border">
                  <p className="text-xs text-muted-foreground">No models yet. Click "Discover Models" to auto-detect, or add manually.</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[200px]">
                  <div className="space-y-1">
                    {providerForm.models.map((m, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-secondary/30 rounded px-3 py-1.5 group">
                        <div className="flex items-center gap-2">
                          <Cpu className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs font-mono">{m.name || m.id}</span>
                          {m.owned_by && <span className="text-[10px] text-muted-foreground">({m.owned_by})</span>}
                          {m.context_window && <Badge variant="outline" className="text-[9px]">{(m.context_window / 1024).toFixed(0)}K ctx</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={() => removeModel(idx)}>
                          <XCircle className="w-3 h-3 text-muted-foreground" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            <Separator />

            {/* Test Connection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Test Connection</Label>
              </div>
              <div className="flex gap-2">
                {providerForm.models.length > 0 && (
                  <Select value={testModel || providerForm.models[0]?.id} onValueChange={setTestModel}>
                    <SelectTrigger className="bg-secondary/40 font-mono text-xs flex-1"><SelectValue placeholder="Select model" /></SelectTrigger>
                    <SelectContent>
                      {providerForm.models.map(m => <SelectItem key={m.id} value={m.id} className="font-mono text-xs">{m.name || m.id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="outline" size="sm" className="text-xs h-9" onClick={handleTestProvider} disabled={providerTesting || providerForm.models.length === 0} data-testid="test-provider-btn">
                  {providerTesting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing...</> : <><TestTube className="w-3 h-3 mr-1" /> Test</>}
                </Button>
              </div>

              {providerTestResult && (
                <div className={`mt-2 p-3 rounded-lg text-xs ${providerTestResult.status === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-destructive/10 border border-destructive/20'}`} data-testid="provider-test-result">
                  <div className="flex items-center gap-2 mb-1">
                    {providerTestResult.status === 'success' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-destructive" />}
                    <span className="font-medium">{providerTestResult.message}</span>
                  </div>
                  {providerTestResult.response && <p className="text-muted-foreground mt-1 bg-secondary/30 rounded p-2 font-mono">"{providerTestResult.response}"</p>}
                  {providerTestResult.duration_s && <p className="text-muted-foreground mt-1">Latency: {providerTestResult.duration_s}s</p>}
                </div>
              )}
            </div>

            <Button onClick={handleSaveProvider} className="w-full bg-[hsl(var(--accent))] text-white" data-testid="save-provider-btn">
              {editingProvider ? 'Update Provider' : 'Add Provider'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
